import { useState, useEffect } from 'react';
import { weekShortLabel } from '@/lib/week-label';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription } from '@/components/ui/modal';
import { API_BASE } from '@/lib/api';
import type { SurvivorPoolData } from '@/services/events/main-event';

interface SurvivorPoolSheetProps {
  mainEventId: string;
  open: boolean;
  onClose: () => void;
}

/**
 * "The Pool" sheet for SURVIVOR events — alive headline plus the per-week
 * attrition list. Fetched lazily on open so the parent pages stay light.
 */
export function SurvivorPoolSheet({ mainEventId, open, onClose }: SurvivorPoolSheetProps) {
  const [pool, setPool] = useState<SurvivorPoolData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !mainEventId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/api/main-events/${mainEventId}/survivor/pool`)
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load pool');
        const data: SurvivorPoolData = await res.json();
        if (!cancelled) setPool(data);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load pool');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, mainEventId]);

  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()}>
      <ModalContent className="max-w-md">
        <ModalHeader>
          <ModalTitle>The Pool</ModalTitle>
          {pool && (
            <ModalDescription>
              Alive {pool.aliveCount} of {pool.totalCount}
            </ModalDescription>
          )}
        </ModalHeader>

        {loading && (
          <div className="flex justify-center py-6">
            <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <p className="text-sm text-oxblood">{error}</p>
        )}

        {pool && !loading && (
          pool.attrition.length === 0 ? (
            <p className="text-sm text-warm-gray">No weeks settled yet.</p>
          ) : (
            <div className="space-y-2.5">
              {pool.attrition.map((row) => (
                <div key={row.round}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-cream">
                      {weekShortLabel(row.round)}: {row.aliveBefore} → {row.aliveAfter}
                    </span>
                    <span className="text-warm-gray">({row.eliminated} out)</span>
                  </div>
                  {/* Thin proportional bar — share of the pool still alive after this week */}
                  <div className="mt-1 h-1 rounded-full bg-surface-3 overflow-hidden">
                    <div
                      className="h-full bg-verdigris"
                      style={{ width: `${(row.aliveAfter / Math.max(pool.totalCount, 1)) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </ModalContent>
    </Modal>
  );
}
